<template>
  <fieldset :disabled="disabled" class="notification-field">
    <div class="d-flex flex-column gap-3">
      <div class="d-flex flex-wrap gap-4">
        <label v-for="color in colorFields" :key="color.key" class="d-flex flex-column gap-1">
          <span>{{ color.label }}</span>
          <input type="color" class="form-control form-control-color" :aria-label="color.label" :value="modelValue[color.key] || defaults[color.key]" :disabled="disabled" @input="update(color.key, $event.target.value)" />
        </label>
      </div>
      <div>
        <label class="d-block">Header logo
          <input class="form-control mt-1" type="file" accept="image/png,image/jpeg" :disabled="disabled || logoBusy" aria-label="Header logo" @change="selectLogo" />
        </label>
        <p class="small text-muted mt-2 mb-2">Ideal: an 800 × 200 px transparent PNG with little surrounding whitespace. PNG or JPEG, up to 2 MB. Images are resized to fit within 800 × 800 px and 100 KB, then fitted without cropping into a 4.33 × 1.07 inch header area. With no logo, the municipality name appears instead.</p>
        <div v-if="logoBusy" class="small" role="status">Preparing logo…</div>
        <div v-if="logoError" class="small text-danger" role="alert">{{ logoError }}</div>
        <div v-if="modelValue.headerLogo" class="border rounded bg-white p-2 mt-2">
          <img :src="modelValue.headerLogo" alt="Certificate header logo" style="display:block;width:100%;max-width:312px;height:77px;object-fit:contain;margin:auto" />
        </div>
        <p v-else-if="modelValue.template === 'scarborough_v1'" class="small text-muted">This saved certificate uses the original Scarborough logo. Upload a replacement or remove it below.</p>
        <button v-if="modelValue.headerLogo || modelValue.template === 'scarborough_v1'" type="button" class="btn btn-sm btn-outline-danger mt-2" :disabled="disabled || logoBusy" @click="setLogo('')">Remove logo</button>
      </div>
      <label>Certificate title
        <input class="form-control" :value="modelValue.title" maxlength="100" :disabled="disabled" @input="update('title', $event.target.value)" />
      </label>
      <label>Authorization wording
        <textarea class="form-control" :value="modelValue.statement" maxlength="700" rows="3" :disabled="disabled" @input="update('statement', $event.target.value)" />
      </label>
      <label>Posting instructions
        <textarea class="form-control" :value="modelValue.postingInstructions" maxlength="200" rows="2" :disabled="disabled" @input="update('postingInstructions', $event.target.value)" />
      </label>
      <label>Local contact name from application
        <select class="form-select" :value="modelValue.localContactNameFieldId" :disabled="disabled" @change="update('localContactNameFieldId', $event.target.value)">
          <option value="">Omit local contact name</option>
          <option v-for="field in fields" :key="field.id" :value="field.id">{{ field.label }}</option>
          <option v-if="missingField(modelValue.localContactNameFieldId)" :value="modelValue.localContactNameFieldId">Unlisted field: {{ modelValue.localContactNameFieldId }}</option>
        </select>
      </label>
      <label>Local contact phone from application
        <select class="form-select" :value="modelValue.localContactPhoneFieldId" :disabled="disabled" @change="update('localContactPhoneFieldId', $event.target.value)">
          <option value="">Omit local contact phone</option>
          <option v-for="field in fields" :key="field.id" :value="field.id">{{ field.label }}</option>
          <option v-if="missingField(modelValue.localContactPhoneFieldId)" :value="modelValue.localContactPhoneFieldId">Unlisted field: {{ modelValue.localContactPhoneFieldId }}</option>
        </select>
      </label>
      <p class="small text-muted mb-0">Missing local contact answers are omitted from the printed license.</p>
    </div>
  </fieldset>
</template>

<script>
import { printableLicenseDefaults } from '@strenforcement/domain/printableLicense';
import { createEmbeddedEmailImage } from '@/emailTemplateImages';

export default {
  props: { modelValue: { type: Object, required: true }, city: Object, disabled: Boolean },
  emits: ['update:modelValue', 'busy'],
  data() {
    return {
      logoBusy: false,
      logoError: null,
      logoRequest: 0,
      defaults: printableLicenseDefaults(),
      colorFields: [
        { key: 'ink', label: 'Ink (text and border)' },
        { key: 'accent', label: 'Accent (highlights)' },
        { key: 'muted', label: 'Muted (labels and dividers)' }
      ]
    };
  },
  beforeUnmount() { this.logoRequest++; this.$emit('busy', false); },
  computed: {
    fields() {
      const form = this.city?.data?.licenseApplicationForm || {};
      const fields = (form.customFields || []).map(field => ({ id: field.id, label: field.label || field.name || field.id }));
      function walk(items) {
        for (const item of items || []) {
          if (item.name && item.type !== 'panel') fields.push({ id: item.name, label: typeof item.title === 'string' ? item.title : item.name });
          walk(item.elements);
        }
      }
      const survey = form.surveyJson || form.survey || {};
      walk(survey.elements);
      for (const page of survey.pages || []) walk(page.elements);
      return [...new Map(fields.filter(field => field.id).map(field => [field.id, field])).values()];
    }
  },
  methods: {
    update(key, value) { this.$emit('update:modelValue', { ...this.modelValue, [key]: value }); },
    setLogo(headerLogo) {
      this.logoError = null;
      this.$emit('update:modelValue', { ...this.modelValue, template: 'municipal_v1', headerLogo });
    },
    async selectLogo(event) {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file) return;
      const request = ++this.logoRequest;
      this.logoBusy = true;
      this.logoError = null;
      this.$emit('busy', true);
      try {
        const image = await createEmbeddedEmailImage(file);
        if (request === this.logoRequest) this.setLogo(image.dataUrl);
      } catch (error) {
        if (request === this.logoRequest) this.logoError = error.message || 'Could not prepare the logo.';
      } finally {
        if (request === this.logoRequest) { this.logoBusy = false; this.$emit('busy', false); }
      }
    },
    missingField(id) { return id && !this.fields.some(field => field.id === id); }
  }
};
</script>
